import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";

import { SubdivisionField } from "@/shared/components/address/SubdivisionField";
import { ColocaDateField } from "@/shared/components/forms/ColocaDateField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import type { DocumentTypeCode } from "@/shared/data/document-types";
import { useCountryLabel } from "@/shared/hooks/useCountryLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import {
  documentLengthMessageKey,
  documentTypeLabelKey,
} from "../../constants/clientDocumentTypes.constants";
import type { CreateOrganizationBody } from "../../types/clients-api.types";
import {
  BUSINESS_IDENTITY_DEFAULTS,
  buildBusinessIdentityRequest,
  buildBusinessIdentitySchema,
  type BusinessIdentityValues,
  useDocumentTypeSync,
} from "./business-identity-form";
import { countryItems } from "./client-form.utils";

// The org row reuses the identity's contact data: name = legal_name, plus the same email/phone/country — one
// form, one source of truth. Shares the nested BusinessIdentityDto schema with the subclient wizard; the org
// `name` is derived from `legal_name`, so it enforces a 2-char minimum here.
const buildOrganizationSchema = (messages: Parameters<typeof buildBusinessIdentitySchema>[0]) =>
  buildBusinessIdentitySchema(messages, { legalNameMinLength: 2 });

const ORGANIZATION_DEFAULTS: BusinessIdentityValues = BUSINESS_IDENTITY_DEFAULTS;

export const ORGANIZATION_WIZARD_STEP_COUNT = 2;

/** Fields validated when leaving each wizard step. */
const STEP_FIELDS: ReadonlyArray<ReadonlyArray<keyof BusinessIdentityValues>> = [
  [
    "legal_name",
    "business_type",
    "country",
    "identification_type",
    "identification_number",
    "incorporation_date",
    "email",
    "phone",
  ],
  [
    "owner_name",
    "owner_country_code",
    "owner_id_type",
    "owner_id_number",
    "owner_address_line1",
    "owner_city",
    "owner_state",
    "owner_postal_code",
  ],
];

export interface OrganizationWizardHandle {
  /** Validates the active step and advances when it is valid. */
  goNext: () => Promise<void>;
  /** Returns to the previous step, keeping everything already captured. */
  goBack: () => void;
}

type OrganizationClientFormProps = {
  formId: string;
  /** Receives the validated body; the host modal performs the request and owns success/error handling. */
  onSubmit: (body: CreateOrganizationBody) => void | Promise<void>;
  /** Reports step changes so the host modal can render the wizard footer (Back / Continue / Create). */
  onStepChange?: (step: number, stepCount: number) => void;
};

/**
 * Root organization create wizard (internal users) — POST /internal/organizations. Two steps aligned with the
 * qa contract: company information (which doubles as the org contact data) → legal representative. The
 * backend derives the KYC address from the owner_* fields, so no address step exists. All steps stay mounted
 * (hidden) so values persist when navigating back; the host modal drives navigation through the imperative
 * `OrganizationWizardHandle`.
 */
export const OrganizationClientForm = forwardRef<OrganizationWizardHandle, OrganizationClientFormProps>(
  function OrganizationClientForm({ formId, onSubmit, onStepChange }, ref) {
    const { t } = useTranslations("clients");
    const countryLabel = useCountryLabel();
    const countryOptions = useMemo(() => countryItems(countryLabel), [countryLabel]);
    const [step, setStep] = useState(0);
    const documentLength = useCallback(
      (code: string) => {
        const key = documentLengthMessageKey(code);
        return key ? t(key) : undefined;
      },
      [t],
    );
    const organizationSchema = useMemo(
      () =>
        buildOrganizationSchema({
          phone: t("validation_phone_required"),
          incorporationDate: t("validation_incorporation_date_invalid"),
          nameFormat: t("validation_name_letters_only"),
          documentLength,
        }),
      [t, documentLength],
    );
    const form = useZodForm(organizationSchema, ORGANIZATION_DEFAULTS);

    // Reported via effect (not inline) so the host stays in sync even when the panel remounts.
    useEffect(() => {
      onStepChange?.(step, ORGANIZATION_WIZARD_STEP_COUNT);
    }, [step, onStepChange]);

    const advanceStep = async () => {
      if (step >= ORGANIZATION_WIZARD_STEP_COUNT - 1) return;
      const valid = await form.trigger(STEP_FIELDS[step] as (keyof BusinessIdentityValues)[]);
      if (valid) setStep(step + 1);
    };

    const retreatStep = () => {
      if (step > 0) setStep(step - 1);
    };

    useImperativeHandle(ref, () => ({ goNext: advanceStep, goBack: retreatStep }));

    const labelFor = useCallback((code: DocumentTypeCode) => t(documentTypeLabelKey(code)), [t]);
    const { companyIdTypeItems, ownerIdTypeItems } = useDocumentTypeSync(form, labelFor);

    const handleSubmit = async (values: BusinessIdentityValues) => {
      const identity = buildBusinessIdentityRequest(values);
      const body: CreateOrganizationBody = {
        name: identity.legal_name,
        email: identity.email,
        identity,
        phone: identity.phone,
        status: "active",
        country: identity.country,
        type: "business",
      };

      await onSubmit(body);
    };

    return (
      <ColocaForm form={form} onSubmit={handleSubmit} id={formId} className="space-y-6">
        {/* Implicit form submission (Enter on an input) must advance the wizard, not submit, until the last
            step. The progress stepper is owned by the host drawer (mock: one global stepper). */}
        <div
          onKeyDown={(event) => {
            if (event.key === "Enter" && step < ORGANIZATION_WIZARD_STEP_COUNT - 1) {
              const target = event.target as HTMLElement;
              if (target.tagName === "INPUT") {
                event.preventDefault();
                void advanceStep();
              }
            }
          }}
          className="space-y-6"
        >
          <section className={cn("space-y-3", step !== 0 && "hidden")} aria-hidden={step !== 0}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ColocaInputField name="legal_name" label={t("modal_label_legal_name")} />
              <ColocaInputField
                name="business_type"
                label={t("modal_label_business_type")}
                placeholder={t("modal_placeholder_business_type")}
              />
              <ColocaSelectField name="country" label={t("modal_label_country")} items={countryOptions} />
              <ColocaSelectField
                name="identification_type"
                label={t("modal_label_identification_type")}
                items={companyIdTypeItems}
              />
              <ColocaInputField name="identification_number" label={t("modal_label_identification_number")} />
              <ColocaDateField
                name="incorporation_date"
                label={t("modal_label_incorporation_date")}
                maxDate={new Date()}
                fromYear={1900}
                toYear={new Date().getFullYear()}
              />
              <ColocaInputField name="email" label={t("modal_label_email")} type="email" />
              <ColocaPhoneField
                name="phone"
                label={t("modal_label_phone")}
                countryFieldName="phone_country"
              />
            </div>
          </section>

          <section className={cn("space-y-3", step !== 1 && "hidden")} aria-hidden={step !== 1}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ColocaInputField name="owner_name" label={t("modal_label_owner_name")} />
              <ColocaSelectField
                name="owner_country_code"
                label={t("modal_label_owner_country_code")}
                items={countryOptions}
              />
              <ColocaSelectField
                name="owner_id_type"
                label={t("modal_label_owner_id_type")}
                items={ownerIdTypeItems}
              />
              <ColocaInputField name="owner_id_number" label={t("modal_label_owner_id_number")} />
              <ColocaInputField name="owner_address_line1" label={t("modal_label_owner_address_line1")} />
              <ColocaInputField name="owner_city" label={t("modal_label_owner_city")} />
              <SubdivisionField
                name="owner_state"
                countryName="owner_country_code"
                label={t("modal_label_owner_state")}
              />
              <ColocaInputField name="owner_postal_code" label={t("modal_label_owner_postal_code")} />
            </div>
          </section>
        </div>
      </ColocaForm>
    );
  },
);
